import React, {useState, useEffect, useContext, useMemo, useCallback, useRef} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Context} from '../index';
import {
    TextField,
    Button,
    Paper,
    Box,
    Checkbox,
    IconButton,
    Dialog,
    DialogContent,
    DialogActions,
  } from '@mui/material';
import {Delete, Add, Remove} from '@mui/icons-material';
import {MaterialReactTable} from "material-react-table";
import MyButton from '../UI/MyButton/MyButton';
import AutocompleteField from '../components/AutocompleteField';
import BarcodeInput from '../components/BarcodeInput';
import PurchaseOrderSelector from '../components/PurchaseOrderSelector';
import GroupedReceiptItems from '../components/GroupedReceiptItems';
import NotificationModal from '../components/NotificationModal';
import ReceiptService from '../services/ReceiptService';
import { formatPrice, formatDiscount, getDiscountColors } from '../utils/currencyUtils';
import ConsignmentConverter from '../utils/ConsignmentConverter';

export default function CreateReceipt() {
    const {store} = useContext(Context);
    const navigate = useNavigate();
    const {id} = useParams();
    const isEdit = Boolean(id);

    
    // Состояние приемки
    const [receipt, setReceipt] = useState({
        store: null, // Будет установлен после загрузки складов
        receiptDate: new Date().toISOString().split('T')[0],
        items: [],
        status: 'draft'
    });

    // Дополнительное состояние для работы с заказами поставщиков
    const [counterparties, setCounterparties] = useState([]); // Список контрагентов в приемке
    const [storesLoading, setStoresLoading] = useState(true); // Загрузка складов
    const [storesLoadError, setStoresLoadError] = useState(false); // Ошибка загрузки складов

    // Состояния UI
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [scannerActive, setScannerActive] = useState(true);
    const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
    const [currentScannedItem, setCurrentScannedItem] = useState(null);
    const [tempQuantity, setTempQuantity] = useState('1');
    const [validationDialog, setValidationDialog] = useState({open: false, message: '', items: []});
    const [noteDialog, setNoteDialog] = useState({open: false, itemUniqueId: null, itemName: '', currentNote: ''});
    const [tempNote, setTempNote] = useState('');

    // // Диалог для подтверждения дубликата товара
    // const [duplicateDialog, setDuplicateDialog] = useState({
    //     open: false,
    //     productName: '',
    //     existingOrders: '',
    //     availableOrder: null,
    //     onConfirm: null
    // });

    // Новые состояния для работы с заказами поставщиков
    const [purchaseOrderSelectorOpen, setPurchaseOrderSelectorOpen] = useState(false);
    const [availableOrders, setAvailableOrders] = useState([]);
    const [currentProductForSelection, setCurrentProductForSelection] = useState(null);

    // Заказы «под клиента»: предупреждение при первом добавлении и склад VIP
    const [vipOrderNotice, setVipOrderNotice] = useState({open: false, orderName: null});
    const vipNoticeShownRef = useRef(new Set());

    // Состояние для управления уведомлениями
    const [notification, setNotification] = useState({
        open: false,
        type: 'success',
        title: '',
        message: '',
        onRetry: null,
        shouldNavigate: false
    });

    // Реф для BarcodeInput компонента
    const barcodeInputRef = useRef(null);

    // Функции для показа уведомлений
    const showSuccess = useCallback((message, title = null, shouldNavigate = false) => {
        setNotification({
            open: true,
            type: 'success',
            title,
            message,
            onRetry: null,
            shouldNavigate
        });
    }, []);

    const showError = useCallback((message, onRetry = null, title = null) => {
        setNotification({
            open: true,
            type: 'error',
            title,
            message,
            onRetry,
            shouldNavigate: false // Для ошибок навигация не нужна
        });
    }, []);

    const closeNotification = useCallback(() => {
        setNotification(prev => {
            const shouldNavigate = prev.type === 'success' && prev.shouldNavigate;
            // Сразу закрываем модальное окно
            const newState = { ...prev, open: false };

            // Если нужно навигировать - делаем это после закрытия
            if (shouldNavigate) {
                setTimeout(() => navigate('/receipts'), 100);
            }

            return newState;
        });
    }, [navigate]);

    // Функция генерации стабильного ID для товара (для существующих товаров)
    const generateStableUniqueId = useCallback((barcode, purchaseOrderId, boxNumber) => {
        const key = `${barcode}_${purchaseOrderId || 'no_order'}_${boxNumber || 1}`;
        // Простая детерминированная хэш-функция
        let hash = 0;
        for (let i = 0; i < key.length; i++) {
            const char = key.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(16).padStart(8, '0');
    }, []);

    // Функция генерации уникального ID для товара (для новых товаров)
    const generateUniqueId = useCallback(() => {
        // randomUUID доступна только в secure context (https/localhost),
        // дев-стенд работает по http - там нужен фолбэк
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }, []);

    // Функции-обработчики
    const handleQuantityChange = useCallback((uniqueId, newQuantity) => {
        const newItems = [...receipt.items];
        const itemIndex = newItems.findIndex(item => item.uniqueId === uniqueId);

        if (itemIndex !== -1) {
            const item = newItems[itemIndex];
            const oldQuantity = item.quantity;

            // Сохраняем исходное количество из заказа при первом добавлении товара
            if (!item.originalQuantity) {
                item.originalQuantity = oldQuantity;
            }

            // Обновляем количество
            item.quantity = newQuantity;

            // Управляем примечанием об изменении количества
            if (item.originalQuantity) {
                const quantityChangeNote = `Изменено количество: было ${item.originalQuantity}, стало ${newQuantity}`;
                const hasQuantityChangeNote = item.note && item.note.includes('Изменено количество:');

                if (newQuantity !== item.originalQuantity) {
                    // Количество изменено - добавляем или обновляем примечание
                    if (hasQuantityChangeNote) {
                        // Обновляем существующее примечание
                        const lines = item.note.split('\n');
                        const updatedLines = lines.map(line =>
                            line.startsWith('Изменено количество:')
                                ? quantityChangeNote
                                : line
                        );
                        item.note = updatedLines.join('\n');
                    } else {
                        // Добавляем новое примечание
                        if (item.note && item.note.trim()) {
                            item.note = `${item.note}\n${quantityChangeNote}`;
                        } else {
                            item.note = quantityChangeNote;
                        }
                    }
                } else {
                    // Количество возвращено к исходному - удаляем примечание об изменении
                    if (hasQuantityChangeNote) {
                        const lines = item.note.split('\n');
                        const filteredLines = lines.filter(line => !line.startsWith('Изменено количество:'));

                        if (filteredLines.length > 0 && filteredLines.some(line => line.trim())) {
                            item.note = filteredLines.join('\n');
                        } else {
                            item.note = null; // Удаляем примечание полностью, если ничего не осталось
                        }
                    }
                }
            }

            setReceipt(prev => ({...prev, items: newItems}));
        }
    }, [receipt.items]);

    // Функция для обновления списка контрагентов
    const updateCounterpartiesList = (items) => {
        const uniqueCounterparties = {};
        items.forEach(item => {
            if (item.agentId && item.agentName) {
                uniqueCounterparties[item.agentId] = {
                    id: item.agentId,
                    name: item.agentName
                };
            }
        });
        setCounterparties(Object.values(uniqueCounterparties));
    };

    // Функция для возврата фокуса на поле сканирования
    const focusBarcodeInput = useCallback(() => {
        setTimeout(() => {
            setScannerActive(true);
            // Проверяем, есть ли у BarcodeInput метод focus()
            if (barcodeInputRef.current && typeof barcodeInputRef.current.focus === 'function') {
                barcodeInputRef.current.focus();
            }
        }, 500); // Небольшая задержка для завершения всех обновлений
    }, [setScannerActive]);

    const handleRemoveItem = useCallback((uniqueId) => {
        const removedItem = receipt.items.find(item => item.uniqueId === uniqueId);
        const newItems = receipt.items.filter(item => item.uniqueId !== uniqueId);

        if (removedItem) {
            // Перенумеруем коробки того же товара
            let boxCounter = 0;
            const renumbered = newItems.map(item => {
                if (item.barcode === removedItem.barcode &&
                    item.purchaseOrderId === removedItem.purchaseOrderId) {
                    boxCounter++;
                    return { ...item, boxNumber: boxCounter };
                }
                return item;
            });
            setReceipt(prev => ({...prev, items: renumbered}));
            updateCounterpartiesList(renumbered);
        } else {
            setReceipt(prev => ({...prev, items: newItems}));
            updateCounterpartiesList(newItems);
        }
    }, [receipt.items, updateCounterpartiesList]);

    const handleOpenNoteDialog = useCallback((uniqueId, itemName, currentNote) => {
        setNoteDialog({
            open: true,
            itemUniqueId: uniqueId,
            itemName,
            currentNote: currentNote || ''
        });
        setTempNote(currentNote || '');
    }, []);

    // Колонки таблицы товаров
    const columns = useMemo(
        () => [
            {
                accessorKey: 'miniature',
                header: 'Фото',
                size: 144,
                Cell: ({ cell }) => (
                    <Box display="flex" justifyContent="center">
                        {cell.getValue() ? (
                            <img
                                src={cell.getValue()}
                                alt="Товар"
                                style={{
                                    width: '96px',
                                    height: '96px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '96px',
                                    height: '96px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: '#999'
                                }}
                            >
                                Нет фото
                            </Box>
                        )}
                    </Box>
                )
            },
            {
                accessorKey: 'barcode',
                header: 'Штрихкод',
                size: 150
            },
            {
                accessorKey: 'name',
                header: 'Наименование',
                size: 250,
                Cell: ({ row }) => {
                    const item = row.original;
                    const hasConsignment = item.consignmentId && item.consignmentLabel;

                    return (
                        <div>
                            <div>{item.name}</div>
                            {hasConsignment && (
                                <div style={{
                                    display: 'inline-block',
                                    marginTop: '4px',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    color: '#1565c0',
                                    backgroundColor: '#e3f2fd',
                                    border: '1px solid #90caf9',
                                    borderRadius: '4px'
                                }}>
                                    Серия: {item.consignmentLabel}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'article',
                header: 'Артикул',
                size: 120
            },
            {
                accessorKey: 'price',
                header: 'Цена',
                size: 120,
                Cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                            {formatPrice(item.price, item.currency)}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'discount',
                header: 'Скидка',
                size: 100,
                Cell: ({ row }) => {
                    const item = row.original;
                    const discountColors = getDiscountColors(item.discount);

                    return (
                        <div style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: discountColors.color,
                            textAlign: 'left',
                        }}>
                            {formatDiscount(item.discount)}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'purchaseOrderName',
                header: 'Заказ поставщика',
                size: 200,
                Cell: ({ row }) => {
                    const orderName = row.original.purchaseOrderName;
                    const cmsOrderName = row.original.cmsOrderName;

                    return (
                        <div style={{ fontSize: '12px', position: 'relative' }}>
                            {orderName || '—'}
                            {cmsOrderName && (
                                <div style={{
                                    fontSize: '12px', // Увеличил с 10px до 12px (~20%)
                                    color: '#f57c00',
                                    marginTop: '4px',
                                    marginLeft: '6px',
                                    fontWeight: 'bold',
                                    border: '1px solid #ff9800',
                                    borderRadius: '3px',
                                    padding: '1px 5px',
                                    backgroundColor: '#fff3e0',
                                    display: 'inline-block'
                                }}>
                                    КМС: {cmsOrderName}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'quantity',
                header: 'Количество',
                size: 120,
                Cell: ({ row, cell }) => {
                    const value = cell.getValue();
                    const index = row.index;
                    const isQuantityChanged = row.original.originalQuantity && value !== row.original.originalQuantity;

                    return (
                        <TextField
                            type="number"
                            value={value}
                            onChange={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                if (newValue >= 0) {
                                    handleQuantityChange(row.original.uniqueId, newValue);
                                }
                            }}
                            inputProps={{
                                min: 0,
                                step: 1,
                                style: {
                                    textAlign: 'center',
                                    fontWeight: isQuantityChanged ? 'bold' : 'normal',
                                    color: isQuantityChanged ? '#f24405' : 'inherit',
                                    backgroundColor: isQuantityChanged ? '#fff3e0' : 'transparent'
                                }
                            }}
                            size="small"
                            sx={{
                                width: '80px',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: isQuantityChanged ? '#ff9800' : '#ddd',
                                        borderWidth: isQuantityChanged ? '2px' : '1px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isQuantityChanged ? '#f57c00' : '#bbb'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#f24405',
                                        borderWidth: '2px'
                                    }
                                }
                            }}
                        />
                    );
                }
            },
            {
                accessorKey: 'note',
                header: 'Примечание',
                size: 120,
                Cell: ({ cell, row }) => {
                    const noteValue = cell.getValue();
                    const hasNote = noteValue !== null && noteValue !== undefined;
                    const isEmpty = noteValue === null || noteValue === undefined || noteValue === '';

                    return (
                        <div
                            onClick={() => handleOpenNoteDialog(row.original.uniqueId, row.original.name, noteValue)}
                            style={{
                                cursor: 'pointer',
                                padding: '5px',
                                borderRadius: '4px',
                                backgroundColor: hasNote ? '#ffebee' : 'transparent',
                                color: hasNote ? '#d32f2f' : '#666',
                                fontSize: '12px',
                                textAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                            }}
                        >
                            {hasNote && <span>🔴</span>}
                            {!hasNote ? '—' : (noteValue === '' ? 'Отмечено' : 'Есть примечание')}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'actions',
                header: 'Действия',
                size: 80,
                enableSorting: false,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={() => handleRemoveItem(row.original.uniqueId)}
                        color="error"
                        size="small"
                    >
                        <Delete />
                    </IconButton>
                )
            }
        ],
        [handleQuantityChange, handleOpenNoteDialog, handleRemoveItem]
    );

    // Загрузка складов и приемки при редактировании
    useEffect(() => {
        // Загружаем склады для установки значения по умолчанию
        const loadDefaultStore = async () => {
            try {
                const response = await ReceiptService.searchStores('');
                const stores = response.data || [];

                // Если склады не загрузились или массив пуст - это ошибка
                if (!stores || stores.length === 0) {
                    throw new Error('Склады не найдены или не удалось загрузить');
                }

                // Ищем склад "Казань, склад А" или похожий
                let defaultStore = stores.find(store =>
                    store.name.toLowerCase().includes('казань') &&
                    store.name.toLowerCase().includes('склад а')
                );

                // Если не нашли, ищем любой склад в Казани
                if (!defaultStore) {
                    defaultStore = stores.find(store =>
                        store.name.toLowerCase().includes('казань')
                    );
                }

                // Если все еще не нашли, берем первый склад
                if (!defaultStore && stores.length > 0) {
                    defaultStore = stores[0];
                }

                if (defaultStore) {
                    setReceipt(prev => {
                        // В режиме черновика склад хранится в самой приемке. Ответ списка складов
                        // может прийти позже загрузки черновика и не должен затирать его склад
                        // (автосейв тогда пере сохранит испорченное значение).
                        if (isEdit && id && prev.store && prev.store.id) {
                            return prev;
                        }
                        return {...prev, store: defaultStore};
                    });
                    setStoresLoadError(false); // Успешная загрузка
                }
            } catch (error) {
                console.error('Error loading default store:', error);
                setStoresLoadError(true); // Устанавливаем флаг ошибки
            } finally {
                setStoresLoading(false);
            }
        };

        loadDefaultStore();

        if (isEdit && id) {
            loadReceipt();
        }
    }, [id, isEdit]);

    // Автосохранение каждые 30 секунд
    useEffect(() => {
        if (!isEdit || receipt.status !== 'draft') return;

        const autoSave = setInterval(() => {
            if (receipt.items.length > 0) {
                handleSaveDraft(true); // silent save
            }
        }, 30000);

        return () => clearInterval(autoSave);
    }, [receipt, isEdit]);

    const loadReceipt = async () => {
        setLoading(true);
        try {
            const response = await ReceiptService.getReceiptById(id);
            const receiptData = response.data;

            // Обрабатываем товары: сначала расставляем boxNumber, потом uniqueId
            const processedItems = (receiptData.items || []).map(item => ({
                ...item,
                originalQuantity: item.originalQuantity || item.quantity
            }));

            // СНАЧАЛА: Расставить boxNumber для старых записей (без boxNumber)
            const boxCounters = {};
            processedItems.forEach(item => {
                const key = `${item.barcode}_${item.purchaseOrderId || 'no_order'}`;
                if (!boxCounters[key]) boxCounters[key] = 0;
                boxCounters[key]++;
                if (!item.boxNumber) {
                    item.boxNumber = boxCounters[key];
                }
            });

            // ПОТОМ: Генерируем uniqueId с уже правильным boxNumber
            processedItems.forEach(item => {
                if (!item.uniqueId) {
                    item.uniqueId = generateStableUniqueId(
                        item.barcode || `unknown_barcode`,
                        item.purchaseOrderId || 'no_order',
                        item.boxNumber
                    );
                }
            });

            setReceipt(prev => ({
                ...receiptData,
                items: processedItems,
                // Сохраняем склад из приемки, если он есть, иначе оставляем текущий (загруженный по умолчанию)
                store: receiptData.store || prev.store,
                receiptDate: receiptData.receiptDate ?
                    (new Date(receiptData.receiptDate).toISOString().split('T')[0]) :
                    new Date().toISOString().split('T')[0]
            }));

            // Обновляем список контрагентов из загруженных данных
            updateCounterpartiesList(processedItems);
        } catch (error) {
            console.error('Error loading receipt:', error);
            alert('Ошибка загрузки приемки');
            navigate('/receipts');
        } finally {
            setLoading(false);
        }
    };

    // Новая функция для загрузки приемки с указанным ID (для использования после отправки)
    const loadReceiptWithId = async (receiptId) => {
        if (!receiptId) {
            console.error('Cannot load receipt: no receiptId provided');
            return;
        }

        try {
            const response = await ReceiptService.getReceiptById(receiptId);
            const receiptData = response.data;

            // Обрабатываем товары: сначала расставляем boxNumber, потом uniqueId
            const processedItems = (receiptData.items || []).map(item => ({
                ...item,
                originalQuantity: item.originalQuantity || item.quantity
            }));

            // СНАЧАЛА: Расставить boxNumber для старых записей (без boxNumber)
            const boxCounters = {};
            processedItems.forEach(item => {
                const key = `${item.barcode}_${item.purchaseOrderId || 'no_order'}`;
                if (!boxCounters[key]) boxCounters[key] = 0;
                boxCounters[key]++;
                if (!item.boxNumber) {
                    item.boxNumber = boxCounters[key];
                }
            });

            // ПОТОМ: Генерируем uniqueId с уже правильным boxNumber
            processedItems.forEach(item => {
                if (!item.uniqueId) {
                    item.uniqueId = generateStableUniqueId(
                        item.barcode || `unknown_barcode`,
                        item.purchaseOrderId || 'no_order',
                        item.boxNumber
                    );
                }
            });

            setReceipt(prev => ({
                ...receiptData,
                items: processedItems,
                // Сохраняем склад из приемки, если он есть, иначе оставляем текущий
                store: receiptData.store || prev.store,
                receiptDate: receiptData.receiptDate ?
                    (new Date(receiptData.receiptDate).toISOString().split('T')[0]) :
                    new Date().toISOString().split('T')[0]
            }));

            // Обновляем список контрагентов из загруженных данных
            updateCounterpartiesList(processedItems);
        } catch (error) {
            console.error('Error loading receipt with ID:', error);
            // Не показываем алерт и не редиректим пользователя, т.к. это может быть фоновая операция
        }
    };

    // Расчёт количества с учётом уже добавленных в текущей приёмке
    // Возвращает null если товар полностью принят
    const calcQuantity = (order, barcode, orderId) => {
        const alreadyInReceipt = receipt.items
            .filter(i => i.barcode === barcode && i.purchaseOrderId === orderId)
            .reduce((sum, i) => sum + i.quantity, 0);

        const trueRemaining = order.remainingQuantity - alreadyInReceipt;

        if (trueRemaining <= 0) {
            return null;
        }

        return order.quantityInBox
            ? Math.min(order.quantityInBox, trueRemaining)
            : trueRemaining;
    };

    // Клиентские заказы (склад VIP) не смешиваются с обычными в одной приемке
    const getMixingConflict = useCallback((isForClient) => {
        const hasClient = receipt.items.some(item => item.isForClient);
        const hasRegular = receipt.items.some(item => item.purchaseOrderId && !item.isForClient);

        if (isForClient && hasRegular) {
            return 'В этой приемке уже есть обычные заказы. Заказ «под клиента» оформите отдельной приемкой';
        }
        if (!isForClient && hasClient) {
            return 'В этой приемке заказ «под клиента» (склад VIP). Обычные заказы оформите отдельной приемкой';
        }
        return null;
    }, [receipt.items]);

    // При добавлении товара из заказа «под клиента»: склад -> VIP на каждом скане
    // (ручная замена сотрудника живет до следующего клиентского скана; при отправке
    // бэк ведет на выбранный склад без принудительного VIP).
    // Предупреждение показываем один раз на заказ
    const handleClientOrderAdded = useCallback((order) => {
        if (order.vipStore) {
            setReceipt(prev => ({...prev, store: order.vipStore}));
        }
        if (vipNoticeShownRef.current.has(order.purchaseOrderId)) return;
        vipNoticeShownRef.current.add(order.purchaseOrderId);
        setVipOrderNotice({open: true, orderName: order.purchaseOrderName});
    }, []);

    const handleBarcodeScanned = async (barcode) => {
        if (!scannerActive || !barcode.trim()) return;

        setScannerActive(false);

        try {
            // Поиск товара в заказах поставщиков
            const response = await ReceiptService.findProductInPurchaseOrders(barcode);
            const searchResult = response.data;


            if (!searchResult.found) {
                showError(`Товар с штрихкодом "${barcode}" не найден в заказах готовых к приемке`);
                setTimeout(() => setScannerActive(true), 1000);
                return;
            }

            const allOrders = searchResult.orders;
            const isConsignment = allOrders[0].type === 'consignment';

            const product = {
                barcode: allOrders[0].barcode,
                productId: allOrders[0].productId,
                productType: allOrders[0].type,
                name: allOrders[0].productName,
                article: allOrders[0].article || '',
                pathName: allOrders[0].pathName || '',
                miniature: allOrders[0].miniature || '',
                // Примечание: устанавливаем quantity в null, чтобы использовать только значение из выбранного заказа
                note: null,
                // Поля серии (consignment) - заполняются если type === 'consignment'
                consignmentId: isConsignment ? allOrders[0].productId : null,
                consignmentName: isConsignment ? allOrders[0].productName : null,
                consignmentLabel: isConsignment ? allOrders[0].label : null,
                consignmentBarcode: isConsignment ? allOrders[0].barcode : null
            };

            // Все заказы доступны для повторного скана (коробочная модель)
            const availableOrders = allOrders;

            // Проверяем: есть ли уже привязка barcode → purchaseOrderId в текущей приёмке
            const existingItem = receipt.items.find(item => item.barcode === product.barcode);

            if (existingItem) {
                // Товар уже добавлен — берём тот же заказ, без диалога
                const boundOrder = allOrders.find(
                    o => o.purchaseOrderId === existingItem.purchaseOrderId
                );

                if (boundOrder) {
                    const existingBoxCount = receipt.items.filter(item =>
                        item.barcode === product.barcode &&
                        item.purchaseOrderId === boundOrder.purchaseOrderId
                    ).length;

                    const qty = calcQuantity(boundOrder, product.barcode, boundOrder.purchaseOrderId);
                    if (!qty) {
                        showError(`Товар "${product.name}" уже полностью принят по заказу ${boundOrder.purchaseOrderName}`);
                        setTimeout(() => setScannerActive(true), 1000);
                        return;
                    }

                    const newItem = {
                        barcode: product.barcode,
                        productId: product.productId,
                        productType: product.productType,
                        name: product.name,
                        article: product.article,
                        pathName: product.pathName,
                        miniature: product.miniature,
                        note: product.note,
                        consignmentId: product.consignmentId,
                        consignmentName: product.consignmentName,
                        consignmentLabel: product.consignmentLabel,
                        consignmentBarcode: product.consignmentBarcode,
                        uniqueId: generateUniqueId(),
                        purchaseOrderId: boundOrder.purchaseOrderId,
                        purchaseOrderName: boundOrder.purchaseOrderName,
                        isForClient: !!boundOrder.isForClient,
                        cmsOrderName: boundOrder.cmsOrderName,
                        agentId: boundOrder.agentId,
                        agentName: boundOrder.agentName,
                        storeId: boundOrder.storeId,
                        storeName: boundOrder.storeName,
                        price: boundOrder.price,
                        discount: boundOrder.discount,
                        currency: boundOrder.currency,
                        boxNumber: existingBoxCount + 1,
                        quantity: qty,
                        originalQuantity: qty
                    };

                    setReceipt(prev => ({
                        ...prev,
                        items: [...prev.items, newItem]
                    }));
                    updateCounterpartiesList([...receipt.items, newItem]);
                    if (boundOrder.isForClient) {
                        handleClientOrderAdded(boundOrder);
                    }
                    focusBarcodeInput();
                } else {
                    // Заказ не найден в allOrders (редкий кейс) — падаем в диалог
                    setCurrentProductForSelection({
                        ...product,
                        allOrders: allOrders,
                        selectedOrderIds: []
                    });
                    setAvailableOrders(availableOrders);
                    setPurchaseOrderSelectorOpen(true);
                }

            } else if (availableOrders.length === 1) {
                // Если один заказ - автоматически добавляем товар
                const selectedOrder = availableOrders[0];

                // Клиентские заказы не смешиваем с обычными
                const mixingConflict = getMixingConflict(selectedOrder.isForClient);
                if (mixingConflict) {
                    showError(mixingConflict);
                    setTimeout(() => setScannerActive(true), 1000);
                    return;
                }

                // Считаем количество коробок этого товара в данном заказе
                const existingBoxCount = receipt.items.filter(item =>
                    item.barcode === product.barcode &&
                    item.purchaseOrderId === selectedOrder.purchaseOrderId
                ).length;

                const qty = calcQuantity(selectedOrder, product.barcode, selectedOrder.purchaseOrderId);
                if (!qty) {
                    showError(`Товар "${product.name}" уже полностью принят по заказу ${selectedOrder.purchaseOrderName}`);
                    setTimeout(() => setScannerActive(true), 1000);
                    return;
                }

                const newItem = {
                    // Базовые данные о товаре
                    barcode: product.barcode,
                    productId: product.productId,
                    productType: product.productType,
                    name: product.name,
                    article: product.article,
                    pathName: product.pathName,
                    miniature: product.miniature,
                    note: product.note,
                    // Поля серии (consignment)
                    consignmentId: product.consignmentId,
                    consignmentName: product.consignmentName,
                    consignmentLabel: product.consignmentLabel,
                    consignmentBarcode: product.consignmentBarcode,
                    // Данные из выбранного заказа
                    uniqueId: generateUniqueId(),
                    purchaseOrderId: selectedOrder.purchaseOrderId,
                    purchaseOrderName: selectedOrder.purchaseOrderName,
                    isForClient: !!selectedOrder.isForClient,
                    cmsOrderName: selectedOrder.cmsOrderName,
                    agentId: selectedOrder.agentId,
                    agentName: selectedOrder.agentName,
                    storeId: selectedOrder.storeId,
                    storeName: selectedOrder.storeName,
                    // Цена, скидка и валюта из заказа
                    price: selectedOrder.price,
                    discount: selectedOrder.discount,
                    currency: selectedOrder.currency,
                    // Номер коробки
                    boxNumber: existingBoxCount + 1,
                    quantity: qty,
                    originalQuantity: qty
                };

                setReceipt(prev => ({
                    ...prev,
                    items: [...prev.items, newItem]
                }));

                // Обновляем список контрагентов
                updateCounterpartiesList([...receipt.items, newItem]);

                if (selectedOrder.isForClient) {
                    handleClientOrderAdded(selectedOrder);
                }

                // Возвращаем фокус на поле сканирования
                focusBarcodeInput();

            } else {
                // Если несколько доступных заказов - открываем диалог выбора
                setCurrentProductForSelection({
                    ...product,
                    allOrders: allOrders,
                    selectedOrderIds: []
                });
                setAvailableOrders(availableOrders);
                setPurchaseOrderSelectorOpen(true);
            }

        } catch (error) {
            console.error('Error finding product:', error);
            showError(`Ошибка при поиске товара с штрихкодом "${barcode}"`);
        }

        // Возвращаем активность сканера через небольшую задержку
        setTimeout(() => setScannerActive(true), 1000);
    };

    // Функция обработки выбора заказа поставщика
    const handlePurchaseOrderSelected = (selectedOrder) => {
        if (!currentProductForSelection || !selectedOrder) return;

        // Клиентские заказы не смешиваем с обычными (страховка от заблокированных карточек)
        const mixingConflict = getMixingConflict(selectedOrder.isForClient);
        if (mixingConflict) {
            showError(mixingConflict);
            return;
        }

        // Считаем количество коробок этого товара в данном заказе
        const existingBoxCount = receipt.items.filter(item =>
            item.barcode === currentProductForSelection.barcode &&
            item.purchaseOrderId === selectedOrder.purchaseOrderId
        ).length;

        const qty = calcQuantity(selectedOrder, currentProductForSelection.barcode, selectedOrder.purchaseOrderId);
        if (!qty) {
            showError(`Товар "${currentProductForSelection.name}" уже полностью принят по заказу ${selectedOrder.purchaseOrderName}`);
            return;
        }

        const newItem = {
            // Базовые данные о товаре из currentProductForSelection
            barcode: currentProductForSelection.barcode,
            productId: currentProductForSelection.productId,
            productType: currentProductForSelection.productType,
            name: currentProductForSelection.name,
            article: currentProductForSelection.article,
            pathName: currentProductForSelection.pathName,
            miniature: currentProductForSelection.miniature,
            note: currentProductForSelection.note,
            // Поля серии (consignment)
            consignmentId: currentProductForSelection.consignmentId,
            consignmentName: currentProductForSelection.consignmentName,
            consignmentLabel: currentProductForSelection.consignmentLabel,
            consignmentBarcode: currentProductForSelection.consignmentBarcode,
            // Данные из выбранного заказа
            uniqueId: generateUniqueId(),
            purchaseOrderId: selectedOrder.purchaseOrderId,
            purchaseOrderName: selectedOrder.purchaseOrderName,
            isForClient: !!selectedOrder.isForClient,
            cmsOrderName: selectedOrder.cmsOrderName,
            agentId: selectedOrder.agentId,
            agentName: selectedOrder.agentName,
            storeId: selectedOrder.storeId,
            storeName: selectedOrder.storeName,
            // Цена, скидка и валюта из заказа
            price: selectedOrder.price,
            discount: selectedOrder.discount,
            currency: selectedOrder.currency,
            // Номер коробки
            boxNumber: existingBoxCount + 1,
            quantity: qty,
            originalQuantity: qty
        };

        setReceipt(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));

        // Обновляем список контрагентов
        updateCounterpartiesList([...receipt.items, newItem]);

        if (selectedOrder.isForClient) {
            handleClientOrderAdded(selectedOrder);
        }

        // Возвращаем фокус на поле сканирования
        focusBarcodeInput();

        // Сбрасываем состояние выбора
        setCurrentProductForSelection(null);
        setAvailableOrders([]);
        setPurchaseOrderSelectorOpen(false);
    };

    const handleQuantityConfirmed = () => {
        if (currentScannedItem && tempQuantity && parseInt(tempQuantity) > 0) {
            const newItem = {
                ...currentScannedItem,
                uniqueId: generateUniqueId(),
                quantity: parseInt(tempQuantity)
            };

            setReceipt(prev => ({
                ...prev,
                items: [...prev.items, newItem]
            }));

            // Обновляем список контрагентов
            updateCounterpartiesList([...receipt.items, newItem]);
        }

        setQuantityDialogOpen(false);
        setCurrentScannedItem(null);
        setTempQuantity('1');
    };

    const handleQuantityDialogKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleQuantityConfirmed();
        }
    };

    const handleNoteChange = (uniqueId, note) => {
        const newItems = [...receipt.items];
        const itemIndex = newItems.findIndex(item => item.uniqueId === uniqueId);

        if (itemIndex !== -1) {
            newItems[itemIndex] = { ...newItems[itemIndex], note };
            setReceipt(prev => ({...prev, items: newItems}));
        }
    };

    const handleCloseNoteDialog = () => {
        setNoteDialog({open: false, itemUniqueId: null, itemName: '', currentNote: ''});
        setTempNote('');
    };

    const handleSaveNote = () => {
        handleNoteChange(noteDialog.itemUniqueId, tempNote === '' ? '' : tempNote);
        handleCloseNoteDialog();
    };

    const handleRemoveNote = () => {
        handleNoteChange(noteDialog.itemUniqueId, null);
        handleCloseNoteDialog();
    };

    // // === СТАРЫЕ ФУНКЦИИ: Обработка диалога дубликата (ЗАКОММЕНТИРОВАНО) ===
    // const handleDuplicateConfirm = () => {
    //     if (duplicateDialog.onConfirm) {
    //         duplicateDialog.onConfirm();
    //     }
    //     setDuplicateDialog({open: false, productName: '', existingOrders: '', availableOrder: null, onConfirm: null});
    //     setTimeout(() => setScannerActive(true), 1000);
    // };

    // const handleDuplicateCancel = () => {
    //     setDuplicateDialog({open: false, productName: '', existingOrders: '', availableOrder: null, onConfirm: null});
    //     setTimeout(() => setScannerActive(true), 1000);
    // };

    const handleSaveDraft = async (silent = false) => {
        if (!receipt.store) {
            if (!silent) alert('Заполните склад');
            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await ReceiptService.updateReceipt(id, receipt);
            } else {
                const response = await ReceiptService.createReceipt(receipt);
                setReceipt(prev => ({...prev, _id: response.data._id}));
                // Обновляем URL без перенаправления
                window.history.replaceState({}, '', `/receipts/${response.data._id}`);
            }

            if (!silent) alert('Черновик сохранен');
        } catch (error) {
            console.error('Error saving draft:', error);
            if (!silent) alert('Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const handleSendToMoysklad = async () => {
        // Валидация
        if (!receipt.store) {
            alert('Заполните обязательное поле: склад');
            return;
        }

        if (receipt.items.length === 0) {
            alert('Добавьте хотя бы один товар');
            return;
        }

        // Проверка, что все товары привязаны к заказам поставщиков
        const itemsWithoutPurchaseOrder = receipt.items.filter(item => !item.purchaseOrderId);
        if (itemsWithoutPurchaseOrder.length > 0) {
            alert('Некоторые товары не привязаны к заказам поставщиков. Проверьте все товары.');
            return;
        }

        await sendReceipt();
    };

    const handleSetDefaultQuantity = async () => {
        // Устанавливаем количество = 1 для всех товаров без количества
        const newItems = receipt.items.map(item => ({
            ...item,
            quantity: item.quantity > 0 ? item.quantity : 1
        }));

        setReceipt(prev => ({...prev, items: newItems}));
        setValidationDialog({open: false, message: '', items: []});

        await sendReceipt();
    };

    const sendReceipt = async () => {
        setSaving(true);
        try {
            let receiptId = id || receipt._id;

            // Проверим статус приемки
            if (receipt.status === 'sent') {
                alert('Приемка уже отправлена в МойСклад');
                return;
            }

            // Если нет ID, сначала создаем приемку
            if (!receiptId) {
                const response = await ReceiptService.createReceipt(receipt);
                receiptId = response.data._id;
                setReceipt(prev => ({...prev, _id: receiptId}));
            } else {
                // Обновляем приемку перед отправкой
                await ReceiptService.updateReceipt(receiptId, receipt);
            }

            // Используем новый эндпоинт для разделения и отправки
            const response = await ReceiptService.splitAndSendReceipt(receiptId);
            const result = response.data;

            // Показываем результаты
            let message = `Обработка завершена!\n\n`;
            message += `Всего заказов: ${result.totalOrders}\n`;
            message += `Успешно отправлено: ${result.successCount}\n`;
            message += `Ошибок: ${result.errorCount}\n\n`;

            if (result.results.length > 0) {
                message += 'Результаты по заказам:\n';
                result.results.forEach((res, index) => {
                    message += `\n${index + 1}. ${res.agentName}\n`;
                    message += `   Заказ: ${res.purchaseOrderName}\n`;
                    message += `   Товаров: ${res.itemsCount}\n`;
                    if (res.success) {
                        message += `   ✅ Отправлено (МойСклад ID: ${res.moyskladId})\n`;
                    } else {
                        message += `   ❌ Ошибка: ${res.error}\n`;
                    }
                });
            }

            // Показываем модальное окно с результатами
            // Определяем тип результата
            const allSuccess = result.successCount === result.totalOrders && result.errorCount === 0;
            const hasSuccess = result.successCount > 0;

            if (allSuccess) {
                // Полный успех - все заказы отправлены успешно
                showSuccess(message, 'Отправка завершена', true);
            } else if (hasSuccess) {
                // Частичный успех - некоторые отправлены, некоторые с ошибками
                message += `\n⚠️ Некоторые приемки отправлены с ошибками.`;
                showError(message, () => sendReceipt(), 'Частичная отправка');
            } else {
                // Полный провал - все попытки с ошибками
                message += `\n❌ Все попытки закончились ошибками.`;
                showError(message, () => sendReceipt(), 'Ошибка отправки');
            }

        } catch (error) {
            console.error('Error sending receipt:', error);

            // Обработка ошибок валидации заказов (Сценарий 4.4)
            if (error.response?.status === 400 && error.response?.data?.invalidItems) {
                const errorData = error.response.data;
                let message = errorData.message + '\n\n';

                if (errorData.invalidItems && errorData.invalidItems.length > 0) {
                    message += 'Проблемные товары:\n';
                    errorData.invalidItems.forEach((item, index) => {
                        message += `\n${index + 1}. ${item.name} (артикул: ${item.barcode || 'не указан'})\n`;
                        message += `   Заказ: ${item.purchaseOrderName || 'ID: ' + item.purchaseOrderId}\n`;
                        message += `   Причина: ${item.reason}\n`;
                    });
                }

                if (errorData.invalidOrders && errorData.invalidOrders.length > 0) {
                    message += '\nПроблемные заказы:\n';
                    errorData.invalidOrders.forEach((order, index) => {
                        message += `\n${index + 1}. Заказ ID: ${order.purchaseOrderId}\n`;
                        message += `   Название: ${order.orderName}\n`;
                        message += `   Причина: ${order.reason}\n`;
                    });
                }

                showError(message, null, 'Ошибка валидации');
            } else {
                // Стандартная обработка других ошибок
                const message = error.response?.data?.message || error.message || 'Ошибка отправки в МойСклад';
                showError(message, () => sendReceipt(), 'Ошибка отправки');
            }
        } finally {
            setSaving(false);
        }
    };

    // Функция для отправки отдельной группы контрагента
    const handleSendGroup = async (group) => {
        // Фильтруем товары только для этой группы
        const groupItems = receipt.items.filter(item =>
            item.purchaseOrderId === group.purchaseOrderId
        );

        if (groupItems.length === 0) {
            alert('Нет товаров для отправки у этого контрагента');
            return;
        }

        setSaving(true);
        try {
            let receiptId = id || receipt._id;

            // Если нет ID, сначала создаем приемку
            if (!receiptId) {
                const response = await ReceiptService.createReceipt(receipt);
                receiptId = response.data._id;
                setReceipt(prev => ({...prev, _id: receiptId}));
            } else {
                // Обновляем приемку перед отправкой
                await ReceiptService.updateReceipt(receiptId, receipt);
            }

            // Отправляем только конкретную группу
            const response = await ReceiptService.sendSpecificGroup(receiptId, group.purchaseOrderId);
            const result = response.data;

            // Перезагружаем данные приемки для обновления статусов, используя гарантированно доступный receiptId
            await loadReceiptWithId(receiptId);

            // Показываем результаты
            let message = `Обработка завершена!\n\n`;
            message += `Контрагент: ${group.agentName}\n`;
            message += `Заказ: ${group.purchaseOrderName}\n`;
            message += `Товаров: ${groupItems.length}\n\n`;

            if (result.success) {
                message += `✅ Успешно отправлено (МойСклад ID: ${result.moyskladId})\n`;
                showSuccess(message);
            } else {
                message += `❌ Ошибка: ${result.error}\n`;
                showError(message, () => handleSendGroup(group), 'Ошибка отправки группы');
            }

        } catch (error) {
            console.error('Error sending group:', error);
            const message = error.response?.data?.message || error.message || 'Ошибка отправки группы';
            showError(message, () => handleSendGroup(group), 'Ошибка отправки группы');
        } finally {
            setSaving(false);
        }
    };

    // Функция для просмотра в МойСклад
    const handleViewInMoysklad = (group) => {
        if (group.result?.moyskladId) {
            window.open(`https://online.moysklad.ru/app/#supply/edit?id=${group.result.moyskladId}`, '_blank');
        }
    };

    if (loading) {
        return <div>Загрузка...</div>;
    }

    return (
        <Box sx={{ padding: 2 }}>
            <Paper sx={{ padding: 3, marginBottom: 2 }}>
                <h4 style={{ marginBottom: '1rem' }}>
                    {isEdit ? 'Редактирование приемки' : 'Новая приемка'}
                </h4>

                {/* Основные поля */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 2, marginBottom: 2 }}>
                    <AutocompleteField
                        label="Склад *"
                        value={receipt.store}
                        onChange={(store) => setReceipt(prev => ({...prev, store}))}
                        searchFunction={ReceiptService.searchStores}
                        placeholder={storesLoading ? "Загрузка складов..." : "Поиск склада..."}
                        required
                        disabled={receipt.status === 'sent' || storesLoading}
                        minSearchLength={0}
                    />

                    <TextField
                        label="Дата приемки"
                        type="date"
                        value={receipt.receiptDate}
                        onChange={(e) => setReceipt(prev => ({...prev, receiptDate: e.target.value}))}
                        InputLabelProps={{ shrink: true }}
                        disabled={receipt.status === 'sent'}
                        margin="dense"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: '#f24405',
                                    borderWidth: '2px'
                                }
                            },
                            '& .MuiInputLabel-root': {
                                '&.Mui-focused': {
                                    color: '#f24405'
                                }
                            }
                        }}
                    />
                </Box>

                {/* Отображение контрагентов из заказов */}
                {counterparties.length > 0 && (
                    <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                        <div style={{ fontSize: '14px', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Контрагенты в приемке:
                        </div>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {counterparties.map((counterparty) => (
                                <Box
                                    key={counterparty.id}
                                    sx={{
                                        padding: '4px 8px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                    }}
                                >
                                    {counterparty.name}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Сканер штрихкодов */}
                {receipt.status === 'draft' && (
                    <BarcodeInput
                        ref={barcodeInputRef}
                        onScan={handleBarcodeScanned}
                        disabled={!scannerActive || !receipt.store || storesLoading || storesLoadError}
                        style={{ marginBottom: 20 }}
                    />
                )}

                {/* Статус и информация */}

                {storesLoadError && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '12px 16px',
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffecb3',
                        borderRadius: '4px',
                        color: '#ff6f00',
                        fontSize: '14px'
                    }}>
                        Не удалось загрузить склады. Пожалуйста, обновите страницу.
                    </div>
                )}

                {receipt.status === 'sent' && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '12px 16px',
                        backgroundColor: '#e8f5e8',
                        border: '1px solid #c8e6c9',
                        borderRadius: '4px',
                        color: '#2e7d32',
                        fontSize: '14px'
                    }}>
                        Приемка отправлена в МойСклад
                    </div>
                )}

                {(receipt.status === 'error' || (receipt.status === 'draft' && receipt.hasSendError)) && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '12px 16px',
                        backgroundColor: '#ffebee',
                        border: '1px solid #ffcdd2',
                        borderRadius: '4px',
                        color: '#c62828',
                        fontSize: '14px'
                    }}>
                        ⚠️ Ошибка отправки
                        {receipt.errorMessage && (
                            <div style={{ marginTop: '4px', fontWeight: 'bold' }}>
                                {receipt.errorMessage}
                            </div>
                        )}
                        {receipt.sendResults && receipt.sendResults.length > 0 && (
                            <div style={{ marginTop: '4px', fontSize: '12px' }}>
                                Последняя ошибка: {
                                    receipt.sendResults[receipt.sendResults.length - 1].error ||
                                    'Ошибка сети'
                                }
                            </div>
                        )}
                        {receipt.status === 'draft' && (
                            <div style={{ marginTop: '4px', fontSize: '12px', fontStyle: 'italic' }}>
                                Вы можете повторить отправку после исправления проблемы
                            </div>
                        )}
                    </div>
                )}
            </Paper>

            {/* Таблица товаров с группировкой по контрагентам */}
            <Paper sx={{ padding: 2 }}>
                <h6 style={{ marginBottom: '1rem' }}>
                    Товары в приемке ({receipt.items.length})
                </h6>

                <GroupedReceiptItems
                    items={receipt.items}
                    sendResults={receipt.sendResults || []}
                    counterparties={counterparties}
                    onQuantityChange={handleQuantityChange}
                    onNoteChange={handleNoteChange}
                    onRemoveItem={handleRemoveItem}
                    onSendGroup={handleSendGroup}
                    onViewInMoysklad={handleViewInMoysklad}
                    disabled={receipt.status === 'sent' || saving}
                    showActions={true}
                    receiptStatus={receipt.status}
                />
            </Paper>

            {/* Кнопки управления */}
            <Box sx={{ display: 'flex', gap: 2, marginTop: 3, justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    onClick={() => navigate('/receipts')}
                    sx={{
                        backgroundColor: '#757575',
                        '&:hover': { backgroundColor: '#616161' },
                        fontSize: '14px',
                        padding: '10px 20px'
                    }}
                >
                    Назад
                </Button>

                {receipt.status === 'draft' && (
                    <>
                        <Button
                            variant="contained"
                            onClick={() => handleSaveDraft(false)}
                            disabled={saving || !receipt.store || storesLoadError}
                            sx={{
                                backgroundColor: '#4caf50',
                                '&:hover': { backgroundColor: '#45a049' },
                                fontSize: '14px',
                                padding: '10px 20px'
                            }}
                        >
                            {saving ? 'Сохранение...' : 'Сохранить черновик'}
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleSendToMoysklad}
                            disabled={saving || !receipt.store || storesLoadError}
                            sx={{
                                backgroundColor: '#f24405',
                                '&:hover': { backgroundColor: '#a62f04' },
                                fontSize: '14px',
                                padding: '10px 20px'
                            }}
                        >
                            {saving ? 'Отправка...' : 'Отправить в МойСклад'}
                        </Button>
                    </>
                )}
            </Box>

            {/* Диалог ввода количества */}
            <Dialog open={quantityDialogOpen} onClose={() => setQuantityDialogOpen(false)}>
                <div style={{ padding: '16px 24px', fontSize: '1.25rem', fontWeight: 500 }}>
                    Укажите количество
                </div>
                <DialogContent>
                    {currentScannedItem && (
                        <Box sx={{ marginBottom: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                            {currentScannedItem.miniature ? (
                                <img
                                    src={currentScannedItem.miniature}
                                    alt="Товар"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd'
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        color: '#999'
                                    }}
                                >
                                    Нет фото
                                </Box>
                            )}
                            <Box>
                                <div><strong>{currentScannedItem.name}</strong></div>
                                <div style={{ fontSize: '14px', color: '#666' }}>
                                    Артикул: {currentScannedItem.article}
                                </div>
                                <div style={{ fontSize: '14px', color: '#666' }}>
                                    Штрихкод: {currentScannedItem.barcode}
                                </div>
                            </Box>
                        </Box>
                    )}
                    <TextField
                        autoFocus
                        label="Количество"
                        type="number"
                        value={tempQuantity}
                        onChange={(e) => setTempQuantity(e.target.value)}
                        onKeyPress={handleQuantityDialogKeyPress}
                        inputProps={{ min: 1, step: 1 }}
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: '#f24405',
                                    borderWidth: '2px'
                                }
                            },
                            '& .MuiInputLabel-root': {
                                '&.Mui-focused': {
                                    color: '#f24405'
                                }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setQuantityDialogOpen(false)}
                        sx={{
                            color: '#757575',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        Отмена
                    </Button>
                    <Button
                        onClick={handleQuantityConfirmed}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' }
                        }}
                    >
                        Добавить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог валидации количества */}
            <Dialog open={validationDialog.open} onClose={() => setValidationDialog({open: false, message: '', items: []})}>
                <div style={{ padding: '16px 24px', fontSize: '1.25rem', fontWeight: 500 }}>
                    Проверьте количество
                </div>
                <DialogContent>
                    <div style={{ marginBottom: '1rem' }}>{validationDialog.message}</div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '0.5rem' }}>
                        Товары без количества:
                    </div>
                    {validationDialog.items.map((item, index) => (
                        <div key={index} style={{ fontSize: '14px' }}>
                            • {item.name}
                        </div>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setValidationDialog({open: false, message: '', items: []})}>
                        Вернуться к редактированию
                    </Button>
                    <Button
                        onClick={handleSetDefaultQuantity}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' }
                        }}
                    >
                        Установить всем количество = 1
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог примечания */}
            <Dialog open={noteDialog.open} onClose={handleCloseNoteDialog} maxWidth="sm" fullWidth>
                <div style={{ padding: '16px 24px', fontSize: '1.25rem', fontWeight: 500 }}>
                    Примечание к товару
                </div>
                <DialogContent>
                    <div style={{ marginBottom: '1rem' }}>
                        <strong>{noteDialog.itemName}</strong>
                    </div>
                    <TextField
                        autoFocus
                        label="Примечание (необязательно)"
                        multiline
                        rows={3}
                        value={tempNote}
                        onChange={(e) => setTempNote(e.target.value)}
                        placeholder="Опишите проблему с товаром или оставьте пустым для простой отметки"
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: '#f24405',
                                    borderWidth: '2px'
                                }
                            },
                            '& .MuiInputLabel-root': {
                                '&.Mui-focused': {
                                    color: '#f24405'
                                }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseNoteDialog}
                        sx={{
                            color: '#757575',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        Отмена
                    </Button>
                    {noteDialog.currentNote !== null && (
                        <Button onClick={handleRemoveNote} color="error">
                            Убрать отметку
                        </Button>
                    )}
                    <Button
                        onClick={handleSaveNote}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' }
                        }}
                    >
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* // === СТАРЫЙ DIALOG: Диалог подтверждения дубликата (ЗАКОММЕНТИРОВАНО) === */}
            {/* Диалог подтверждения дубликата товара */}
            {/*
            <Dialog open={duplicateDialog.open} onClose={handleDuplicateCancel} maxWidth="sm" fullWidth>
                <div style={{ padding: '16px 24px', fontSize: '1.25rem', fontWeight: 500 }}>
                    Обнаружен дубликат товара
                </div>
                <DialogContent>
                    <div style={{ marginBottom: '1rem', fontSize: '16px' }}>
                        Товар "<strong>{duplicateDialog.productName}</strong>" уже был добавлен в приемку.
                    </div>

                    {duplicateDialog.existingOrders && (
                        <div style={{ marginBottom: '1rem', fontSize: '14px', color: '#666' }}>
                            <strong>Уже добавлен в заказы:</strong><br/>
                            {duplicateDialog.existingOrders}
                        </div>
                    )}

                    <div style={{ marginBottom: '1rem', fontSize: '14px', color: '#666' }}>
                        <strong>Доступен для добавления в заказ:</strong><br/>
                        {duplicateDialog.availableOrder?.purchaseOrderName} ({duplicateDialog.availableOrder?.agentName})
                    </div>

                    <div style={{ padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', fontSize: '14px' }}>
                        <strong>Внимание!</strong><br/>
                        После нажатия "Подтвердить" товар будет добавлен еще раз в указанный заказ.
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDuplicateCancel}
                        sx={{
                            color: '#757575',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        Отмена
                    </Button>
                    <Button
                        onClick={handleDuplicateConfirm}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' }
                        }}
                    >
                        Подтвердить
                    </Button>
                </DialogActions>
            </Dialog>
            */}

            {/* Диалог выбора заказа поставщика */}
            <PurchaseOrderSelector
                open={purchaseOrderSelectorOpen}
                orders={availableOrders}
                allOrders={currentProductForSelection?.allOrders}
                selectedOrderIds={currentProductForSelection?.selectedOrderIds || []}
                hasClientOrders={receipt.items.some(item => item.isForClient)}
                hasRegularOrders={receipt.items.some(item => item.purchaseOrderId && !item.isForClient)}
                productName={currentProductForSelection?.name || ''}
                productBarcode={currentProductForSelection?.barcode || ''}
                productImage={currentProductForSelection?.miniature || ''}
                onClose={() => {
                    setPurchaseOrderSelectorOpen(false);
                    setCurrentProductForSelection(null);
                    setAvailableOrders([]);
                }}
                onSelect={handlePurchaseOrderSelected}
            />

            {/* Предупреждение о заказе «под клиента» */}
            <Dialog
                open={vipOrderNotice.open}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setVipOrderNotice(prev => ({...prev, open: false}));
                    }
                }}
                disableEscapeKeyDown
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: {
                        border: '4px solid #ffc107',
                        borderRadius: '12px'
                    }
                }}
            >
                <DialogContent sx={{ textAlign: 'center', paddingTop: '32px' }}>
                    <div style={{ fontSize: '22px', fontWeight: 'bold', marginBottom: '12px' }}>
                        Клиентский заказ №{vipOrderNotice.orderName}
                    </div>
                    <div style={{ fontSize: '18px' }}>
                        Принять на склад VIP
                    </div>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', paddingBottom: '24px' }}>
                    <Button
                        onClick={() => setVipOrderNotice(prev => ({...prev, open: false}))}
                        variant="contained"
                        sx={{
                            backgroundColor: '#ffc107',
                            color: '#000',
                            '&:hover': { backgroundColor: '#ffb300' },
                            fontSize: '16px',
                            padding: '10px 32px'
                        }}
                    >
                        Понятно
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Модальное окно уведомлений */}
            <NotificationModal
                open={notification.open}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                onRetry={notification.onRetry}
                onClose={closeNotification}
            />
        </Box>
    );
}